import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Play,
  Settings,
  Plus,
  ArrowRight,
  Workflow,
  BookTemplate,
  Plug,
  HelpCircle,
  ChevronRight,
  Search,
  Star,
  BookOpen,
  MessageCircle,
  Video,
  Mail,
  Bell,
  Palette,
  Trash2,
  ChevronLeft,
  RefreshCw,
  FileText,
  Copy,
  Pencil,
  Shapes,
  LayoutGrid,
  List,
  AlertTriangle,
  Pin,
  History,
  Download,
  Upload,
  Database,
  Code,
  Bot,
  Sparkles,
  X,
  Diamond,
} from "lucide-react";
import { toast } from "sonner";
import Sidebar from "../components/ui/Sidebar";
import SimpleBpmnEditor from "../../components/bpmn/SimpleBpmnEditor";
import type { SimpleBpmnEditorRef } from "../../components/bpmn/SimpleBpmnEditor";
import CreateWorkflowModal from "../components/studio/CreateWorkflowModal";
import CreateFormModal from "../components/studio/CreateFormModal";
import DeleteFormModal from "../components/studio/DeleteFormModal";
import CreateConnectorModal from "../components/studio/CreateConnectorModal";
import DeployModal from "../components/studio/DeployModal";
import VersionHistoryModal from "../components/studio/VersionHistoryModal";
import CreateDatatableModal from "../components/studio/CreateDatatableModal";
import FormBuilderPage from "./FormBuilderPage";
import AiChatSidebar from "../components/studio/AiChatSidebar";
import MedicalBotStudio from "../components/studio/MedicalBotStudio";
import ConnectorBuilderPage from "./ConnectorBuilderPage";
import DecisionsPage from "./DecisionsPage";
import RulesPage from "./RulesPage";
import StartProcessDialog from "../../components/processes/StartProcessDialog";
import {
  getWorkflows,
  createWorkflow,
  deleteWorkflow,
  getWorkflowContent,
  getWorkflowContentByVersion,
  getDesignerTemplates,
  getWorkflowVersions,
  decodeBpmnXML,
} from "../../services/workflowService";

// Function to enrich AI-generated service tasks with template metadata
async function enrichServiceTasksWithTemplates(xml: string): Promise<string> {
  try {
    // Sanitize XML first - remove invalid patterns that AI sometimes generates
    let sanitizedXml = xml.replace(/<=>/g, "");

    // Fetch all templates
    const templates = await getDesignerTemplates();

    // Parse XML
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(sanitizedXml, "text/xml");

    // Find all service tasks (support both bpmn: and bpmn2: namespaces)
    const serviceTasks1 = Array.from(xmlDoc.getElementsByTagName("bpmn:serviceTask"));
    const serviceTasks2 = Array.from(xmlDoc.getElementsByTagName("bpmn2:serviceTask"));
    const allServiceTasks = [...serviceTasks1, ...serviceTasks2];

    if (allServiceTasks.length === 0) {
      return sanitizedXml; // No service tasks to enrich, but return sanitized XML
    }

    let modified = false;

    // Process each service task
    for (const task of allServiceTasks) {
      const camundaClass = task.getAttribute("camunda:class");

      if (!camundaClass) continue;

      // Find matching template by implementation class
      const matchingTemplate = templates.find((template) => {
        if (template.jsonContent) {
          try {
            const decoded = atob(template.jsonContent);
            const json = JSON.parse(decoded);
            return json.implementation === camundaClass;
          } catch (e) {
            return false;
          }
        }
        return false;
      });

      if (matchingTemplate && matchingTemplate.jsonContent) {
        try {
          const decoded = atob(matchingTemplate.jsonContent);
          const json = JSON.parse(decoded);

          // Add camunda:customElement if not present
          if (json.topic && !task.getAttribute("camunda:customElement")) {
            task.setAttribute("camunda:customElement", json.topic);
            modified = true;
          }

          // Add camunda:modelerTemplateIcon if not present
          if (json.modelerTemplateIcon && !task.getAttribute("camunda:modelerTemplateIcon")) {
            task.setAttribute("camunda:modelerTemplateIcon", json.modelerTemplateIcon);
            modified = true;
          }

          // Add custom:properties if not present and we have properties
          if (json.properties && !task.getAttribute("custom:properties")) {
            task.setAttribute("custom:properties", JSON.stringify(json.properties));
            modified = true;
          }
        } catch (e) {
          console.warn("Error enriching service task:", e);
        }
      }
    }

    if (!modified) {
      return sanitizedXml; // No changes needed, but return sanitized XML
    }

    // Ensure custom namespace is declared in definitions
    const definitions = xmlDoc.getElementsByTagName("bpmn:definitions")[0] ||
      xmlDoc.getElementsByTagName("bpmn2:definitions")[0] ||
      xmlDoc.getElementsByTagName("definitions")[0];

    if (definitions && !definitions.getAttribute("xmlns:custom")) {
      definitions.setAttribute("xmlns:custom", "http://custom");
    }

    // Serialize back to XML
    const serializer = new XMLSerializer();
    return serializer.serializeToString(xmlDoc);
  } catch (error) {
    console.error("Error enriching service tasks:", error);
    // Return sanitized XML on error to ensure invalid patterns are removed
    return xml.replace(/<=>/g, "");
  }
}
import { getForms, deleteForm } from "../../services/formService";
import type { Form } from "../../services/formService";
import type { Workflow as WorkflowType } from "../../services/workflowService";
import type {
  DesignerTemplate,
  WorkflowContent,
} from "../../services/workflowService";
import {
  getDatatables,
  createDatatable,
  deleteDatatable,
  getTableData,
  deleteRecord,
} from "../../services/datatableService";
import type { Datatable } from "../../services/datatableService";

import { templates } from "./studioConstants";
import type { WorkflowTemplate } from "./studioConstants";

// Left sidebar navigation items for Studio. Mirrors `StudioSubSidebar`
// — keep both in lock-step so the menu looks identical whether the user
// is on /studio (this page) or on /studio/templateEngine (TemplateEnginePage).
const studioNavItems = [
  { key: "create", label: "Create", icon: <Plus size={18} />, active: true },
  { key: "workflows", label: "Workflows", icon: <Workflow size={18} /> },
  { key: "decisions", label: "Decisions", icon: <Diamond size={18} /> },
  { key: "rules", label: "Rules", icon: <List size={18} /> },
  { key: "forms", label: "Forms", icon: <FileText size={18} /> },
  { key: "database", label: "Database", icon: <Database size={18} /> },
  { key: "templates", label: "Templates", icon: <BookTemplate size={18} /> },
  { key: "templateEngine", label: "Template Engine", icon: <Shapes size={18} /> },
  { key: "connectors", label: "Components", icon: <Plug size={18} /> },
  { key: "preferences", label: "Preferences", icon: <Settings size={18} /> },
  { key: "agenticAi", label: "Agentic AI", icon: <Bot size={18} /> },
  { key: "help", label: "Help guide", icon: <HelpCircle size={18} /> },
];

const StudioPage: React.FC = () => {
  const { workflowSlug, formSlug, connectorId } = useParams<{
    workflowSlug?: string;
    formSlug?: string;
    connectorId?: string;
  }>();
  const navigate = useNavigate();

  // Helper to check if slug is a valid tab
  const isTab = (slug?: string) => {
    return studioNavItems.some((item) => item.key === slug);
  };

  const [isSaving, setIsSaving] = useState(false);

  const [pinnedConnectorIds, setPinnedConnectorIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("pinnedConnectorIds");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  const [versions, setVersions] = useState<WorkflowContent[]>([]);
  // const [isLoadingVersions, setIsLoadingVersions] = useState(false);

  const toggleConnectorPin = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPinnedConnectorIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((p) => p !== id)
        : [...prev, id];
      localStorage.setItem("pinnedConnectorIds", JSON.stringify(next));
      return next;
    });
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<WorkflowType | null>(
    null,
  );

  const [currentFormJson, setCurrentFormJson] = useState<string>("");

  // `?nav=<key>` lets `StudioSubSidebar` (on /studio/templateEngine etc.)
  // jump to a specific tab inside StudioPage — currently used for
  // Agentic AI and Help guide, which are tabs here rather than routes.
  const [searchParams] = useSearchParams();
  const navParam = searchParams.get("nav");

  const [activeNavItem, setActiveNavItem] = useState(() => {
    if (connectorId) return "connectorBuilder";
    if (formSlug) return "formBuilder";
    if (workflowSlug && isTab(workflowSlug)) return workflowSlug; // It's a tab
    if (workflowSlug) return "editor"; // It's a workflow
    if (navParam === "agenticAi" || navParam === "help") return navParam;
    return "create";
  });

  // Sync activeNavItem with URL params
  useEffect(() => {
    if (connectorId) {
      setActiveNavItem("connectorBuilder");
    } else if (formSlug) {
      setActiveNavItem("formBuilder");
    } else if (workflowSlug) {
      if (isTab(workflowSlug)) {
        setActiveNavItem(workflowSlug);
      } else {
        setActiveNavItem("editor");
      }
    } else if (navParam === "agenticAi" || navParam === "help") {
      // `?nav=` deep-link from the sub-sidebar of another Studio page.
      setActiveNavItem(navParam);
    } else {
      // If no params, default to create only if we're not explicitly setting 'forms' via internal navigation
      // But actually, URL should be source of truth.
      // If URL is /studio, we should default to 'create' or whatever the default is.
      if (
        activeNavItem !== "create" &&
        activeNavItem !== "forms" &&
        activeNavItem !== "workflows" &&
        activeNavItem !== "connectors" &&
        activeNavItem !== "templates" &&
        activeNavItem !== "preferences" &&
        activeNavItem !== "help" &&
        activeNavItem !== "agenticAi"
      ) {
        setActiveNavItem("create");
      }
    }
  }, [workflowSlug, formSlug, connectorId, navParam]);

  const [isCreateWorkflowModalOpen, setIsCreateWorkflowModalOpen] =
    useState(false);
  const [isCreateFormModalOpen, setIsCreateFormModalOpen] = useState(false);
  const [workflowSearchQuery, setWorkflowSearchQuery] = useState("");
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowType | null>(
    null,
  );

  // Template state
  const [selectedCategory, setSelectedCategory] = useState<
    "All" | "Popular" | "Marketing" | "Sales" | "HR" | "Finance" | "IT"
  >("All");
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [selectedTemplate, setSelectedTemplate] =
    useState<WorkflowTemplate | null>(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [templateWorkflowName, setTemplateWorkflowName] = useState("");
  const [isCreatingFromTemplate, setIsCreatingFromTemplate] = useState(false);

  // API state (Workflows)
  const [workflows, setWorkflows] = useState<WorkflowType[]>([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [totalWorkflows, setTotalWorkflows] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const workflowsHasLoaded = useRef(false);

  // API state (Forms)
  const [forms, setForms] = useState<Form[]>([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [totalForms, setTotalForms] = useState(0);
  const formsHasLoaded = useRef(false);
  const [formsCurrentPage, setFormsCurrentPage] = useState(0);
  const [formsPageSize, setFormsPageSize] = useState(10);
  const [formsTotalPages, setFormsTotalPages] = useState(0);
  const [formSearchQuery, setFormSearchQuery] = useState("");

  // Elements (Designer Templates) state

  const [designerTemplates, setDesignerTemplates] = useState<
    DesignerTemplate[]
  >([]);
  const [loadingDesignerTemplates, setLoadingDesignerTemplates] =
    useState(false);
  const [connectorViewMode, setConnectorViewMode] = useState<"grid" | "list">(
    "grid",
  );
  const [connectorFilter, setConnectorFilter] = useState<
    "All" | "Manual" | "Trigger"
  >("All");

  const [connectorSearchQuery, setConnectorSearchQuery] = useState("");
  const [isCreateConnectorModalOpen, setIsCreateConnectorModalOpen] =
    useState(false);

  // Agentic AI (Gen AI app list) state
  const GENAI_STORAGE_KEY = "genAiCustomCards";
  const defaultAgenticApps: {
    id: string;
    title: string;
    text: string;
    type: string;
    workflowType?: string;
    isBuiltIn: boolean;
  }[] = [
      {
        id: "medical-bot",
        title: "Medical Bot",
        text: "An intelligent assistant for medical queries and workflow automation.",
        type: "workflow",
        workflowType: "medical",
        isBuiltIn: true,
      },
    ];
  const [agenticApps, setAgenticApps] = useState<typeof defaultAgenticApps>(
    () => {
      try {
        const stored = localStorage.getItem(GENAI_STORAGE_KEY);
        const custom = stored ? JSON.parse(stored) : [];
        const customMapped = Array.isArray(custom)
          ? custom.map((c: any, i: number) => ({
            id: c.id || `custom-${i}`,
            title: c.title || "Untitled App",
            text: c.text || "",
            type: c.type || "workflow",
            workflowType: c.workflowType || "medical",
            isBuiltIn: false,
          }))
          : [];
        return [...defaultAgenticApps, ...customMapped];
      } catch {
        return defaultAgenticApps;
      }
    },
  );
  const [isCreateAppModalOpen, setIsCreateAppModalOpen] = useState(false);
  const [isCreateFromBlankModalOpen, setIsCreateFromBlankModalOpen] =
    useState(false);
  const [newAppName, setNewAppName] = useState("");
  const [newAppDesc, setNewAppDesc] = useState("");
  const [newAppWorkflowDesc, setNewAppWorkflowDesc] = useState("");
  const [agenticSearchQuery, setAgenticSearchQuery] = useState("");
  const [agenticTabFilter, setAgenticTabFilter] = useState<
    "All" | "Workflow" | "Agent"
  >("All");
  const [agenticCreatedByMeOnly, setAgenticCreatedByMeOnly] = useState(false);
  const [agenticSelectOption, setAgenticSelectOption] = useState("All");
  const [agenticAiSubView, setAgenticAiSubView] = useState<
    null | "medical-bot"
  >(null);

  const [editingConnector, setEditingConnector] = useState<{
    id?: string;
    name: string;
    description?: string;
    type?: "manual" | "trigger" | "INBOUND" | "OUTBOUND";
    category?: string;
    icon?: string;
    modelerTemplateIcon?: string;
    implementation?: string;
    properties?: any[];
  } | null>(null);

  // Database (Datatable) state
  const [datatables, setDatatables] = useState<Datatable[]>([]);
  const [isLoadingDatatables, setIsLoadingDatatables] = useState(false);
  const [datatableSearchQuery, setDatatableSearchQuery] = useState("");
  const [selectedDatatable, setSelectedDatatable] = useState<Datatable | null>(
    null,
  );
  const [isCreateDatatableModalOpen, setIsCreateDatatableModalOpen] =
    useState(false);
  const [datatableRecords, setDatatableRecords] = useState<
    Record<string, any>[]
  >([]);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsCurrentPage] = useState(1);
  const [recordsPageSize] = useState(10);
  const datatableHasLoaded = useRef(false);

  // Form Builder state
  const [editingFormId, setEditingFormId] = useState<string | undefined>(
    undefined,
  );

  // Deploy Modal state
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [deployBpmnXml, setDeployBpmnXml] = useState<string>("");
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);

  // Start Process state
  const [isStartProcessOpen, setIsStartProcessOpen] = useState(false);
  const [startProcessDefId, setStartProcessDefId] = useState("");
  const [startProcessName, setStartProcessName] = useState("");
  const [startProcessVersion, setStartProcessVersion] = useState("");

  // AI Assistant Drawer state
  const [isAiDrawerOpen, setIsAiDrawerOpen] = useState(false); // Controls mount/unmount
  const [isAiDrawerVisible, setIsAiDrawerVisible] = useState(false); // Controls animation

  // Handle smooth open: mount first, then animate
  useEffect(() => {
    if (isAiDrawerOpen) {
      // Small delay so the browser renders the component off-screen first
      const timer = setTimeout(() => setIsAiDrawerVisible(true), 20);
      return () => clearTimeout(timer);
    }
  }, [isAiDrawerOpen]);

  // Handle smooth close
  const handleCloseAiDrawer = () => {
    setIsAiDrawerVisible(false); // Trigger slide-out animation
    setTimeout(() => setIsAiDrawerOpen(false), 520); // Unmount after 500ms CSS transition + small buffer
  };

  const [currentBpmnXml, setCurrentBpmnXml] = useState("");

  // Editor ref for save functionality
  const editorRef = useRef<SimpleBpmnEditorRef>(null);

  // Sync the latest XML from the editor when the drawer opens or workflow changes
  useEffect(() => {
    if (isAiDrawerVisible && editorRef.current) {
      editorRef.current.getXml().then((xml) => setCurrentBpmnXml(xml || ""));
    }
  }, [isAiDrawerVisible, selectedWorkflow]);

  // Align with doit-ui form page: when on workflow editor, clear form context so AI shows process context only
  useEffect(() => {
    if (activeNavItem === "editor") {
      setCurrentFormJson("");
    }
  }, [activeNavItem]);

  // Close AI drawer when leaving a form or workflow
  useEffect(() => {
    if (activeNavItem !== "editor" && activeNavItem !== "formBuilder") {
      if (isAiDrawerOpen) {
        handleCloseAiDrawer();
      }
    }
  }, [activeNavItem]);

  // File input ref for import
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle BPMN file import
  const handleImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const xml = e.target?.result as string;
      if (xml && editorRef.current) {
        await editorRef.current.importXml(xml);
      }
    };
    reader.readAsText(file);

    // Reset file input so the same file can be imported again
    event.target.value = "";
  };

  // Load workflow or form from URL slug
  useEffect(() => {
    if (workflowSlug) {
      if (isTab(workflowSlug)) {
        setActiveNavItem(workflowSlug as any);
      } else {
        setActiveNavItem("editor");

        // Fetch workflow details if not selected or slug mismatch
        if (!selectedWorkflow || selectedWorkflow.nameSlug !== workflowSlug) {
          const loadWorkflow = async () => {
            try {
              const content = await getWorkflowContent(workflowSlug);
              if (content && content.process) {
                setSelectedWorkflow(content.process);
              }

              // Load versions for the workflow
              const workflowVersions = await getWorkflowVersions(workflowSlug);
              setVersions(workflowVersions);
            } catch (error) {
              console.error("Error loading workflow from slug:", error);
              // Only show error if it's not likely a new creation or handled elsewhere
              // toast.error('Failed to load workflow');
            }
          };
          loadWorkflow();
        }
      }
    } else if (formSlug) {
      setActiveNavItem("formBuilder");

      // If we have forms loaded, find the ID
      if (forms.length > 0) {
        const form = forms.find((f) => f.nameSlug === formSlug);
        if (form) {
          setEditingFormId(form.id);
        }
      } else {
        // If forms aren't loaded, we need to fetch them using the existing fetchForms
        fetchForms();
      }
    } else if (connectorId) {
      setActiveNavItem("connectorBuilder");
      if (connectorId === "new") {
        if (!editingConnector) {
          // If navigating directly to /new without modal data, set defaults
          // But if coming from modal, we might have set state.
          // Ideally we shouldn't rely on state if user refreshes.
          // For now, if no state, we initialize empty.
          setEditingConnector({
            name: "New Connector",
            description: "",
            type: "INBOUND",
            properties: [],
          });
        }
      } else {
        // Fetch or find existing
        if (designerTemplates.length > 0) {
          const template = designerTemplates.find((t) => t.id === connectorId);
          if (template) {
            // Parse and set
            let properties = [];
            let icon = template.icon;
            let implementation = "com.pmdg.delegates.GenericDelegate";
            let modelerTemplateIcon = "";
            let description = template.description || "";

            try {
              if (template.jsonContent) {
                const jsonString = atob(template.jsonContent);
                const decoded = JSON.parse(jsonString);
                if (decoded.properties) properties = decoded.properties;
                if (decoded.icon) icon = decoded.icon;
                if (decoded.implementation)
                  implementation = decoded.implementation;
                if (decoded.modelerTemplateIcon)
                  modelerTemplateIcon = decoded.modelerTemplateIcon;
                if (decoded.description) description = decoded.description;
              }
            } catch (e) {
              console.error(e);
            }

            setEditingConnector({
              id: template.id,
              name: template.name,
              description: description,
              type: template.type as any,
              category: template.category,
              icon,
              implementation,
              modelerTemplateIcon,
              properties,
            });
          }
        } else {
          fetchDesignerTemplates();
        }
      }
    } else {
      // Root /studio path
      setActiveNavItem("create");
    }
  }, [
    workflowSlug,
    formSlug,
    connectorId,
    forms.length,
    designerTemplates.length,
  ]); // specialized dependency on lengths to trigger on load

  // Fetch designer templates (Elements)
  const fetchDesignerTemplates = useCallback(async () => {
    setLoadingDesignerTemplates(true);
    try {
      const templates = await getDesignerTemplates();
      setDesignerTemplates(templates);
    } catch (error) {
      console.error("Error fetching designer templates:", error);
      toast.error("Failed to load elements");
    } finally {
      setLoadingDesignerTemplates(false);
    }
  }, []);

  // Effect to load data based on active tab
  useEffect(() => {
    if (activeNavItem === "connectors") {
      fetchDesignerTemplates();
    }
  }, [activeNavItem, fetchDesignerTemplates]);

  // Fetch datatables from API
  const fetchDatatables = useCallback(async () => {
    if (!datatableHasLoaded.current || datatables.length === 0) {
      setIsLoadingDatatables(true);
    }
    try {
      const data = await getDatatables();
      setDatatables(data);
      datatableHasLoaded.current = true;
    } catch (error) {
      console.error("Error fetching datatables:", error);
      toast.error("Failed to load database tables");
    } finally {
      setIsLoadingDatatables(false);
    }
  }, [datatables.length]);

  // Effect to load datatables when database tab is active
  useEffect(() => {
    if (activeNavItem === "database") {
      fetchDatatables();
    }
  }, [activeNavItem, fetchDatatables]);

  // Fetch records for selected datatable
  const fetchDatatableRecords = useCallback(
    async (collectionName: string) => {
      setIsLoadingRecords(true);
      try {
        const data = await getTableData(
          collectionName,
          recordsCurrentPage,
          recordsPageSize,
        );
        setDatatableRecords(data.documentList);
        setTotalRecords(data.totalRecords);
      } catch (error) {
        console.error("Error fetching records:", error);
        toast.error("Failed to load table records");
      } finally {
        setIsLoadingRecords(false);
      }
    },
    [recordsCurrentPage, recordsPageSize],
  );

  // Effect to load records when a datatable is selected
  useEffect(() => {
    if (selectedDatatable) {
      fetchDatatableRecords(selectedDatatable.mongoCollectionName);
    }
  }, [selectedDatatable, fetchDatatableRecords]);

  // Fetch templates for Overview if empty
  useEffect(() => {
    if (
      activeNavItem === "create" &&
      designerTemplates.length === 0 &&
      !loadingDesignerTemplates
    ) {
      fetchDesignerTemplates();
    }
  }, [
    activeNavItem,
    designerTemplates.length,
    loadingDesignerTemplates,
    fetchDesignerTemplates,
  ]);

  // Random connectors for Overview
  const randomConnectors = React.useMemo(() => {
    if (designerTemplates.length <= 4) return designerTemplates;
    const shuffled = [...designerTemplates].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 4);
  }, [designerTemplates]);

  // Fetch workflows from API
  const fetchWorkflows = useCallback(async () => {
    // Only show loading skeleton on initial load, not on refresh/search
    if (!workflowsHasLoaded.current || workflows.length === 0) {
      setWorkflowsLoading(true);
    }
    try {
      const response = await getWorkflows(
        currentPage,
        pageSize,
        workflowSearchQuery || undefined,
      );
      setWorkflows(response.content);
      setTotalWorkflows(response.totalElements);
      setTotalPages(response.totalPages);
      workflowsHasLoaded.current = true;
    } catch (error) {
      console.error("Error fetching workflows:", error);
      toast.error("Failed to load workflows");
    } finally {
      setWorkflowsLoading(false);
    }
  }, [workflowSearchQuery, currentPage, pageSize, workflows.length]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeNavItem === "workflows") {
        fetchWorkflows();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [workflowSearchQuery, currentPage, pageSize, activeNavItem]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(0);
  }, [workflowSearchQuery]);

  // Handle workflow selection from URL
  useEffect(() => {
    if (workflowSlug && workflows.length === 0) {
      // Initial load for deep link if needed, logic mostly covered by first useEffect
    }
  }, [workflowSlug]);

  // Fetch forms from API
  const fetchForms = useCallback(async () => {
    // Only show loading skeleton on initial load, not on refresh/search
    if (!formsHasLoaded.current || forms.length === 0) {
      setFormsLoading(true);
    }
    try {
      const response = await getForms(
        formsCurrentPage,
        formsPageSize,
        formSearchQuery || undefined,
        "createdDate,DESC",
      );
      setForms(response.content);
      setTotalForms(response.totalElements);
      setFormsTotalPages(response.totalPages);
      formsHasLoaded.current = true;
    } catch (error) {
      console.error("Error fetching forms:", error);
      toast.error("Failed to load forms");
    } finally {
      setFormsLoading(false);
    }
  }, [formSearchQuery, formsCurrentPage, formsPageSize, forms.length]);

  // Debounce form search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeNavItem === "forms") {
        fetchForms();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [
    formSearchQuery,
    formsCurrentPage,
    formsPageSize,
    activeNavItem,
    fetchForms,
  ]);

  // Reset form page when search changes
  useEffect(() => {
    setFormsCurrentPage(0);
  }, [formSearchQuery]);

  // Delete Form Modal state
  const [formToDelete, setFormToDelete] = useState<Form | null>(null);
  const [isDeletingForm, setIsDeletingForm] = useState(false);

  // Handle form delete click
  const handleDeleteForm = (id: string) => {
    const form = forms.find((f) => f.id === id);
    if (form) {
      setFormToDelete(form);
    }
  };

  // Confirm delete form
  const handleConfirmDeleteForm = async () => {
    if (!formToDelete) return;

    setIsDeletingForm(true);
    try {
      await deleteForm(formToDelete.id);
      toast.success("Form deleted successfully");
      setFormToDelete(null); // Close modal
      fetchForms();
    } catch (error) {
      console.error("Error deleting form:", error);
      toast.error("Failed to delete form");
    } finally {
      setIsDeletingForm(false);
    }
  };

  // Copy form URL to clipboard
  const copyFormUrl = (form: Form) => {
    const url = `api/form-contents/${form.nameSlug}`;
    navigator.clipboard.writeText(url);
    toast.success("Form URL copied to clipboard");
  };

  const handleWorkflowClick = (workflow: WorkflowType) => {
    const slug =
      workflow.nameSlug || workflow.name?.toLowerCase().replace(/\s+/g, "_");
    navigate(`/studio/${slug}`);
  };

  const handleBackFromEditor = () => {
    setSelectedWorkflow(null);
    navigate("/studio/workflows");
  };

  const handleDeleteWorkflow = async (id: string) => {
    try {
      await deleteWorkflow(id);
      toast.success("Workflow deleted successfully");
      fetchWorkflows();
    } catch (error) {
      console.error("Error deleting workflow:", error);
      toast.error("Failed to delete workflow");
    }
  };

  const handleCreateWorkflowContinue = async (data: any) => {
    console.log("Workflow data:", data);
    try {
      // Create workflow via API
      let description = data.workflowDescription || "A new automated workflow";
      if (data.triggerType === "ai" && data.aiPrompt) {
        description = `AI Generated: ${data.aiPrompt} `;
      }

      const newWorkflow = await createWorkflow({
        name: data.workflowName || "New Workflow",
        description,
        triggerType: data.triggerType,
        version: "0.1",
      });
      toast.success("Workflow created successfully");
      setSelectedWorkflow(newWorkflow);
      setActiveNavItem("editor");
      // Navigate using the backend-returned nameSlug for consistency
      navigate(`/studio/${newWorkflow.nameSlug}`);
    } catch (error) {
      console.error("Error creating workflow:", error);
      toast.error("Failed to create workflow");
    }
  };

  // Handle template selection - open modal to get workflow name
  const handleTemplateSelect = (template: WorkflowTemplate) => {
    setSelectedTemplate(template);
    setTemplateWorkflowName(template.title);
    setIsTemplateModalOpen(true);
  };

  // Create workflow from template
  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate || !templateWorkflowName.trim()) {
      toast.error("Please enter a workflow name");
      return;
    }

    setIsCreatingFromTemplate(true);
    try {
      // Pass the template BPMN as initialBpmn so it's used directly
      const newWorkflow = await createWorkflow({
        name: templateWorkflowName,
        description: selectedTemplate.description,
        triggerType: "manual",
        version: "0.1",
        initialBpmn: selectedTemplate.bpmnTemplate, // Pass template BPMN directly
      });

      toast.success("Workflow created from template!");
      setIsTemplateModalOpen(false);
      setSelectedTemplate(null);
      setTemplateWorkflowName("");
      setSelectedWorkflow(newWorkflow);
      setActiveNavItem("editor");
      // Use the nameSlug returned from backend to ensure consistency
      navigate(`/studio/${newWorkflow.nameSlug}`);
    } catch (error) {
      console.error("Error creating workflow from template:", error);
      toast.error("Failed to create workflow from template");
    } finally {
      setIsCreatingFromTemplate(false);
    }
  };

  const handleCreateConnector = async (data: {
    name: string;
    description: string;
    type: "manual" | "trigger";
    category: string;
  }) => {
    // Preparing data for the builder
    setEditingConnector({
      name: data.name,
      description: data.description,
      type: data.type === "manual" ? "INBOUND" : "OUTBOUND",
      category: data.category,
      implementation: "com.pmdg.delegates.GenericDelegate",
      properties: [],
    });
    setIsCreateConnectorModalOpen(false);
    navigate("/studio/connectors/new");
  };

  // Agentic AI: Create App from Blank
  const handleCreateFromBlankSubmit = () => {
    if (!newAppName.trim()) {
      toast.error("Please enter an app name");
      return;
    }
    let workflowType: string = "medical";
    let finalDesc = newAppDesc.trim() || "An intelligent assistant.";
    const descLower = (newAppWorkflowDesc || "").toLowerCase();
    if (descLower.includes("loan")) {
      workflowType = "loan";
      finalDesc =
        "A specialized AI assistant for processing loan applications, assessing creditworthiness, and providing personalized financial advice.";
    } else if (descLower.includes("support")) {
      workflowType = "support";
      finalDesc =
        "An intelligent customer support agent designed to handle inquiries, troubleshoot issues, and provide timely assistance.";
    }
    const newApp = {
      id: `custom-${Date.now()}`,
      title: newAppName.trim(),
      text: finalDesc,
      type: "workflow",
      workflowType,
      isBuiltIn: false,
    };
    setAgenticApps((prev) => [...prev, newApp]);
    try {
      const existing = JSON.parse(
        localStorage.getItem(GENAI_STORAGE_KEY) || "[]",
      );
      localStorage.setItem(
        GENAI_STORAGE_KEY,
        JSON.stringify([
          ...existing,
          {
            id: newApp.id,
            title: newApp.title,
            text: newApp.text,
            type: newApp.type,
            workflowType: newApp.workflowType,
          },
        ]),
      );
    } catch (e) {
      console.error("Failed to save to localStorage", e);
    }
    setIsCreateFromBlankModalOpen(false);
    setIsCreateAppModalOpen(false);
    setNewAppName("");
    setNewAppDesc("");
    setNewAppWorkflowDesc("");
    toast.success("App created successfully");
  };

  const handleDeleteAgenticApp = (app: (typeof agenticApps)[0]) => {
    if (app.isBuiltIn) return;
    setAgenticApps((prev) => prev.filter((a) => a.id !== app.id));
    try {
      const existing = JSON.parse(
        localStorage.getItem(GENAI_STORAGE_KEY) || "[]",
      );
      const updated = existing.filter(
        (c: any) => c.id !== app.id && c.title !== app.title,
      );
      localStorage.setItem(GENAI_STORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
      console.error("Failed to update localStorage", e);
    }
    toast.success("App removed");
  };

  const handleConfigureConnector = (template: DesignerTemplate) => {
    navigate(`/studio/connectors/${template.id}`);
  };

  // Filter templates based on category and search query
  const filteredTemplates = templates.filter((template) => {
    const matchesCategory =
      selectedCategory === "All" ||
      (selectedCategory === "Popular" && template.popular) ||
      template.category === selectedCategory;

    const matchesSearch =
      !templateSearchQuery ||
      template.title
        .toLowerCase()
        .includes(templateSearchQuery.toLowerCase()) ||
      template.description
        .toLowerCase()
        .includes(templateSearchQuery.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  /* Logic for refresh */
  const handleRefresh = async () => {
    // Reload versions
    if (selectedWorkflow?.nameSlug) {
      getWorkflowVersions(selectedWorkflow.nameSlug).then(setVersions);

      // Reload workflow content
      try {
        const content = await getWorkflowContent(selectedWorkflow.nameSlug);
        if (content && content.process) {
          // Update workflow metadata (e.g. status, dates) but keep editing state if possible?
          // Actually we likely want to sync everything.
          setSelectedWorkflow(content.process);
        }
      } catch (e) {
        console.error("Failed to refresh workflow", e);
      }
    }

    // Refresh editor
    editorRef.current?.refresh();

    toast.info("Refreshed");
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await editorRef.current?.save();
      // Refresh versions after save and update to latest version
      if (selectedWorkflow?.nameSlug) {
        const updatedVersions = await getWorkflowVersions(
          selectedWorkflow.nameSlug,
        );
        setVersions(updatedVersions);
        // Update selectedWorkflow with the latest version
        if (updatedVersions.length > 0) {
          const latestVersion = updatedVersions[0]?.version; // First item is latest (sorted DESC)
          if (latestVersion) {
            setSelectedWorkflow((prev) =>
              prev ? { ...prev, version: latestVersion } : null,
            );
          }
        } else {
          // If no versions array, try to get from single content response
          const content = await getWorkflowContent(selectedWorkflow.nameSlug);
          if (content?.version) {
            setSelectedWorkflow((prev) =>
              prev ? { ...prev, version: content.version } : null,
            );
          }
        }
      }
    } catch (e) {
      console.error("Save failed", e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeploy = async () => {
    if (!selectedWorkflow?.nameSlug || isSaving) return;

    // Get current BPMN XML
    const xml = await editorRef.current?.getXml();
    if (!xml) {
      toast.error("Unable to get workflow XML");
      return;
    }

    setDeployBpmnXml(xml);
    setIsDeployModalOpen(true);
  };

  const handleDeploySuccess = (definitionId: string, definitionKey: string) => {
    // Update local state to show deployed with definition info
    setSelectedWorkflow((prev) =>
      prev
        ? {
          ...prev,
          status: "DEPLOYED",
          definitionId,
          definitionKey,
          lastModifiedDate: new Date().toISOString(),
        }
        : null,
    );

    // Refresh versions
    if (selectedWorkflow?.nameSlug) {
      getWorkflowVersions(selectedWorkflow.nameSlug).then(setVersions);
    }
  };

  const renderOverview = () => (
    <div className="flex-1 overflow-auto">
      <div className="max-w-6xl mx-auto p-8">
        {/* Hero Section */}
        <div className="flex items-start justify-between mb-12">
          <div className="max-w-xl">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              Create automated processes with ease
            </h1>
            <p className="text-gray-600 mb-6">
              Elevate your productivity, streamline your workflow, collaborate
              seamlessly with your team, and automate your processes all in one
              place.
            </p>
            <button
              onClick={() => setIsCreateWorkflowModalOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all shadow-md hover:shadow-lg"
            >
              Start from scratch
            </button>
          </div>

          {/* Watch Demo Section */}
          <div className="flex flex-col items-center gap-2">
            <div className="relative w-32 h-32">
              {/* Animated circles/lines decoration */}
              <svg className="w-full h-full" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="3" fill="#6366f1" />
                <circle cx="30" cy="30" r="2" fill="#a855f7" />
                <circle cx="70" cy="35" r="2" fill="#ec4899" />
                <circle cx="25" cy="60" r="2" fill="#06b6d4" />
                <circle cx="75" cy="65" r="2" fill="#10b981" />
                <line
                  x1="30"
                  y1="30"
                  x2="50"
                  y2="50"
                  stroke="#e5e7eb"
                  strokeWidth="1"
                />
                <line
                  x1="70"
                  y1="35"
                  x2="50"
                  y2="50"
                  stroke="#e5e7eb"
                  strokeWidth="1"
                />
                <line
                  x1="25"
                  y1="60"
                  x2="50"
                  y2="50"
                  stroke="#e5e7eb"
                  strokeWidth="1"
                />
                <line
                  x1="75"
                  y1="65"
                  x2="50"
                  y2="50"
                  stroke="#e5e7eb"
                  strokeWidth="1"
                />
              </svg>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center">
                <Play size={14} className="text-white ml-0.5" fill="white" />
              </div>
              <span className="text-sm font-medium text-gray-700">
                Watch demo
              </span>
            </div>
          </div>
        </div>

        {/* Start from template Section */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Start from template
              </h2>
              <p className="text-sm text-gray-500">
                Choose from our existing templates to get started right away.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {templates.map((template, index) => (
              <div
                key={template.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
              >
                {/* Gradient header with icons */}
                <div
                  className={`h-24 bg-gradient-to-br ${template.gradient} flex items-center justify-center gap-2 relative`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center text-white font-bold text-sm">
                      {template.icons[0].letter || template.icons[0].icon}
                    </div>
                    <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center text-white font-bold text-sm">
                      {template.icons[1].letter || template.icons[1].icon}
                    </div>
                  </div>

                  {/* Show count badge on last card */}
                  {index === templates.length - 1 && (
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur px-2 py-1 rounded-md text-xs font-semibold text-gray-700">
                      +2670
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">
                    {template.title}
                  </h3>
                  <p className="text-xs text-gray-500 line-clamp-2">
                    {template.description}
                  </p>
                </div>
              </div>
            ))}

            {/* View all templates link */}
            <div className="lg:col-span-4 flex justify-end mt-2">
              <button
                onClick={() => navigate("/studio/templates")}
                className="inline-flex items-center gap-1 text-sm font-medium text-teal-600 hover:text-teal-700"
              >
                View all templates <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Start from connectors Section */}
        <div>
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Start from connectors
            </h2>
            <p className="text-sm text-gray-500">
              Choose from activities and mini-connectors within your favourite
              active connectors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {randomConnectors.map((connector) => {
              // Extract icon and description from jsonContent
              let iconUrl: string | undefined = connector.icon;
              let fallbackIcon = connector.name.charAt(0).toUpperCase();

              try {
                if (connector.jsonContent) {
                  const decoded = atob(connector.jsonContent);
                  const json = JSON.parse(decoded);
                  if (json.modelerTemplateIcon) {
                    iconUrl = json.modelerTemplateIcon;
                  }
                }
              } catch (e) {
                // ignore json parse error
              }
              return (
                <div
                  key={connector.id}
                  className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => handleConfigureConnector(connector)}
                >
                  {/* Header with icon and active badge */}
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg overflow-hidden`}
                    >
                      {iconUrl ? (
                        <img
                          src={iconUrl}
                          alt={connector.name}
                          className="w-6 h-6 object-contain"
                        />
                      ) : (
                        <span>{fallbackIcon}</span>
                      )}
                    </div>
                    {/* Reuse category as badge if no "active" concept on template yet, or just hardcode for demo */}
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      Active
                    </span>
                  </div>

                  {/* Content */}
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {connector.name}
                  </h3>
                  <p className="text-xs text-gray-500 mb-4 line-clamp-2">
                    {connector.description || "No description available"}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between">
                    <button className="text-sm font-medium text-teal-600 hover:text-teal-700">
                      Configure
                    </button>
                    <button className="text-gray-400 hover:text-gray-600">
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
            {randomConnectors.length === 0 && (
              <div className="col-span-4 text-center py-8 text-gray-400">
                Loading connectors...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderEditor = () => (
    <div className="flex flex-col h-full">
      {/* Editor Header - Redesigned */}
      <div className="bg-white border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
        {/* Left Section - Breadcrumb Navigation */}
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={handleBackFromEditor}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
          >
            <Workflow size={18} />
          </button>
          <ChevronRight size={14} className="text-gray-400" />
          <span className="font-medium text-gray-900">
            {selectedWorkflow?.name || "Model # 1"}
          </span>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right Section - Actions */}

        <div className="flex items-center gap-3">
          {/* AI Assistant Button */}
          <button
            onClick={() => {
              if (isAiDrawerOpen) {
                handleCloseAiDrawer();
              } else {
                setIsAiDrawerOpen(true);
              }
            }}
            className={`p-2 rounded-lg transition-colors ${isAiDrawerOpen
                ? "bg-indigo-100 text-indigo-600"
                : "hover:bg-gray-100 text-indigo-600"
              }`}
            title="AI Assistant"
          >
            <Sparkles size={18} />
          </button>

          <button
            onClick={handleRefresh}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>

          {/* Import Button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
            title="Import BPMN"
          >
            <Upload size={18} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".bpmn,.xml"
            className="hidden"
            onChange={handleImportFile}
          />

          {/* Export Button */}
          <button
            onClick={() => editorRef.current?.exportXml()}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
            title="Export BPMN"
          >
            <Download size={18} />
          </button>

          {selectedWorkflow?.status === "DEPLOYED" && selectedWorkflow.definitionKey && selectedWorkflow.definitionId && (
            <button
              onClick={() => {
                setStartProcessDefId(selectedWorkflow.definitionId!);
                setStartProcessName(selectedWorkflow.name);
                setStartProcessVersion(selectedWorkflow.version || "0.1");
                setIsStartProcessOpen(true);
              }}
              className="px-4 py-2 text-sm font-medium bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors shadow-sm flex items-center gap-2"
            >
              <Play size={16} fill="currentColor" /> Run Process
            </button>
          )}

          {/* Version Selector */}
          {selectedWorkflow && (
            <button
              onClick={() => setIsVersionModalOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors"
              title="Version History"
            >
              <History size={16} className="text-gray-500" />
              <span className="text-gray-700">
                v{selectedWorkflow.version || "0.1"}
              </span>
            </button>
          )}

          <div className="h-6 w-px bg-gray-200" />

          {selectedWorkflow?.status === "NOTDEPLOYED" ? (
            <span className="text-sm text-gray-400 italic">Not deployed</span>
          ) : (
            <span className="text-sm text-gray-600">
              {selectedWorkflow?.lastModifiedDate
                ? `Last deployed on ${new Date(selectedWorkflow.lastModifiedDate).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })} (v${selectedWorkflow.version || "0.1"})`
                : "Deployed"}
            </span>
          )}

          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 shadow-sm"
          >
            {isSaving ? "Saving..." : "Save"}
          </button>

          <button
            onClick={handleDeploy}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-all disabled:opacity-50 shadow-sm"
          >
            Deploy
          </button>

          <button
            onClick={handleBackFromEditor}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Editor Canvas Container with AI Drawer */}
      <div className="flex-1 relative overflow-hidden">
        {/* BPMN Editor - This will now fill 100% of the space */}
        <SimpleBpmnEditor
          ref={editorRef}
          workflow={selectedWorkflow}
          onSave={(updatedWorkflow) => setSelectedWorkflow(updatedWorkflow)}
        />

        {/* AI Drawer moved to portal - see end of return */}
      </div>

      {/* Version History Modal */}
      <VersionHistoryModal
        isOpen={isVersionModalOpen}
        onClose={() => setIsVersionModalOpen(false)}
        versions={versions.map((v) => ({
          version: v.version,
          id: v.id,
          createdDate: v.process?.createdDate,
        }))}
        currentVersion={selectedWorkflow?.version || "0.1"}
        onPreview={async (version: string) => {
          if (!selectedWorkflow?.nameSlug) return;

          toast.loading("Loading version preview...", {
            id: "version-preview",
          });
          try {
            const content = await getWorkflowContentByVersion(
              selectedWorkflow.nameSlug,
              version,
            );

            // Decode and load the BPMN into the editor
            if (content?.bpmn && editorRef.current) {
              const xml = decodeBpmnXML(content.bpmn);
              await editorRef.current.importXml(xml);
            }

            toast.success(`Previewing version ${version} (unsaved)`, {
              id: "version-preview",
            });
          } catch (error) {
            console.error("Failed to preview:", error);
            toast.error("Failed to load version preview", {
              id: "version-preview",
            });
            throw error;
          }
        }}
        onRevert={async (version: string) => {
          if (!selectedWorkflow?.nameSlug) return;

          toast.loading("Restoring version...", { id: "version-revert" });
          try {
            const content = await getWorkflowContentByVersion(
              selectedWorkflow.nameSlug,
              version,
            );

            // Decode and load the BPMN into the editor
            if (content?.bpmn && editorRef.current) {
              const xml = decodeBpmnXML(content.bpmn);
              await editorRef.current.importXml(xml);
            }

            // Update the selected workflow version
            setSelectedWorkflow((prev) => (prev ? { ...prev, version } : null));
            toast.success(
              `Restored version ${version} - Save to create a new version`,
              { id: "version-revert" },
            );
          } catch (error) {
            console.error("Failed to restore:", error);
            toast.error("Failed to restore version", { id: "version-revert" });
            throw error;
          }
        }}
        title="Workflow Version History"
      />
    </div>
  );

  // Workflows Screen
  const renderWorkflows = () => (
    <div className="flex-1 overflow-auto">
      <div className="max-w-6xl mx-auto p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Workflows</h1>
            <p className="text-gray-500 text-sm">
              Manage and monitor your automated workflows
            </p>
          </div>
          <button
            onClick={() => setIsCreateWorkflowModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus size={18} />
            New Workflow
          </button>
        </div>

        {/* Search and Refresh */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={workflowSearchQuery}
              onChange={(e) => setWorkflowSearchQuery(e.target.value)}
              placeholder="Search workflows..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <button
            onClick={() => fetchWorkflows()}
            disabled={workflowsLoading}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw
              size={16}
              className={workflowsLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>

        {/* Workflow List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {workflowsLoading ? (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Process
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Modified
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                        <div className="h-3 bg-gray-100 rounded w-24"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-28"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-12"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="h-6 bg-gray-200 rounded w-6 ml-auto"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : workflows.length === 0 ? (
            <div className="text-center py-12">
              <Workflow size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">No workflows found</p>
              <button
                onClick={() => setIsCreateWorkflowModalOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <Plus size={16} />
                Create your first workflow
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Process
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Modified
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {workflows.map((workflow) => (
                  <tr
                    key={workflow.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleWorkflowClick(workflow)}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {workflow.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {workflow.description || workflow.nameSlug}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${workflow.status === "DEPLOYED"
                            ? "bg-green-100 text-green-700"
                            : workflow.status === "NOTDEPLOYED"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                      >
                        {workflow.status === "DEPLOYED"
                          ? "Deployed"
                          : workflow.status === "NOTDEPLOYED"
                            ? "Not Deployed"
                            : "Paused"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {workflow.definitionId ? (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/processes?definition=${workflow.definitionKey}`,
                              );
                            }}
                            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                            title={`View process: ${workflow.definitionId}`}
                          >
                            View
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setStartProcessDefId(workflow.definitionId!);
                              setStartProcessName(workflow.name);
                              setStartProcessVersion(workflow.version || "0.1");
                              setIsStartProcessOpen(true);
                            }}
                            className="text-sm text-teal-600 hover:text-teal-800 hover:underline flex items-center gap-1"
                            title={`Start process: ${workflow.definitionId}`}
                          >
                            <Play size={12} fill="currentColor" /> Run
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {workflow.version || "0.1"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {workflow.lastModifiedDate
                        ? new Date(
                          workflow.lastModifiedDate,
                        ).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          className={`p-1 hover:bg-gray-100 rounded ${workflow.status === "DEPLOYED" ? "text-gray-300 cursor-not-allowed" : "text-gray-400 hover:text-red-500"}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (workflow.status === "DEPLOYED") {
                              toast.error(
                                "Cannot delete a deployed workflow. Please undeploy it first.",
                              );
                              return;
                            }
                            setWorkflowToDelete(workflow);
                            setIsDeleteModalOpen(true);
                          }}
                          title={
                            workflow.status === "DEPLOYED"
                              ? "Undeploy first to delete"
                              : "Delete workflow"
                          }
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Total count */}
        {!workflowsLoading && workflows.length > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
            <div>
              Showing {currentPage * pageSize + 1} to{" "}
              {Math.min((currentPage + 1) * pageSize, totalWorkflows)} of{" "}
              {totalWorkflows} workflows
            </div>

            <div className="flex items-center gap-2">
              <span className="mr-2">Rows per page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(0);
                }}
                className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-indigo-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>

              <div className="flex items-center gap-1 ml-4">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="mx-2">
                  Page {currentPage + 1} of {totalPages || 1}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  disabled={currentPage >= totalPages - 1}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Forms Screen
  const renderForms = () => (
    <div className="flex-1 overflow-auto">
      <div className="max-w-6xl mx-auto p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Forms</h1>
            <p className="text-gray-500 text-sm">Manage your dynamic forms </p>
          </div>
          <button
            onClick={() => setIsCreateFormModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus size={18} />
            New Form
          </button>
        </div>

        {/* Search and Refresh */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={formSearchQuery}
              onChange={(e) => setFormSearchQuery(e.target.value)}
              placeholder="Search forms..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <button
            onClick={() => fetchForms()}
            disabled={formsLoading}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw
              size={16}
              className={formsLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>

        {/* Form List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {formsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-gray-500">Loading forms...</span>
            </div>
          ) : forms.length === 0 ? (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">No forms found</p>
              <button
                onClick={() => setIsCreateFormModalOpen(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Plus size={18} />
                Create your first form
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Process Key
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {forms.map((form) => (
                  <tr key={form.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {form.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {form.description || form.nameSlug}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {form.version || "0.1"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {form.processKey || "-"}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-indigo-500"
                          onClick={() => copyFormUrl(form)}
                          title="Copy Form URL"
                        >
                          <Copy size={16} />
                        </button>
                        <button
                          className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-blue-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingFormId(form.id);
                            // Navigate to form builder with slug
                            const slug =
                              form.nameSlug ||
                              form.name
                                .trim()
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")
                                .replace(/(^-|-$)/g, "");
                            navigate(`/studio/forms/${slug}`);
                          }}
                          title="Edit Form"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteForm(form.id);
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!formsLoading && forms.length > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
            <div>
              Showing {formsCurrentPage * formsPageSize + 1} to{" "}
              {Math.min((formsCurrentPage + 1) * formsPageSize, totalForms)} of{" "}
              {totalForms} forms
            </div>

            <div className="flex items-center gap-2">
              <span className="mr-2">Rows per page:</span>
              <select
                value={formsPageSize}
                onChange={(e) => {
                  setFormsPageSize(Number(e.target.value));
                  setFormsCurrentPage(0);
                }}
                className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-indigo-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>

              <div className="flex items-center gap-1 ml-4">
                <button
                  onClick={() => setFormsCurrentPage((p) => Math.max(0, p - 1))}
                  disabled={formsCurrentPage === 0}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="mx-2">
                  Page {formsCurrentPage + 1} of {formsTotalPages || 1}
                </span>
                <button
                  onClick={() =>
                    setFormsCurrentPage((p) =>
                      Math.min(formsTotalPages - 1, p + 1),
                    )
                  }
                  disabled={formsCurrentPage >= formsTotalPages - 1}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Form Builder Screen
  const renderFormBuilder = () => (
    <FormBuilderPage
      formId={editingFormId}
      onFormChange={(json) => setCurrentFormJson(json)}
      onAiClick={() => setIsAiDrawerOpen(true)}
      onBack={() => {
        setEditingFormId(undefined);
        setCurrentFormJson(""); // Clear form context on back
        setActiveNavItem("forms");
        navigate("/studio/forms");
        fetchForms(); // Refresh list
      }}
      onSave={() => {
        fetchForms(); // Refresh list on save
      }}
      initialFormJson={currentFormJson}
    />
  );

  // Templates Screen
  const renderTemplates = () => (
    <div className="flex-1 overflow-auto">
      <div className="max-w-6xl mx-auto p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
            <p className="text-gray-500 text-sm">
              Browse and use pre-built workflow templates
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search templates..."
                value={templateSearchQuery}
                onChange={(e) => setTemplateSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 w-64"
              />
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {(
            [
              "All",
              "Popular",
              "Marketing",
              "Sales",
              "HR",
              "Finance",
              "IT",
            ] as const
          ).map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCategory === category
                  ? "bg-teal-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                } `}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Template Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
              onClick={() => handleTemplateSelect(template)}
            >
              <div
                className={`h-32 bg-gradient-to-br ${template.gradient} flex items-center justify-center gap-3 relative`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center text-white font-bold text-xl">
                    {template.icons[0].letter || template.icons[0].icon}
                  </div>
                  <ArrowRight size={20} className="text-white/60" />
                  <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center text-white font-bold text-xl">
                    {template.icons[1].letter || template.icons[1].icon}
                  </div>
                </div>
                {/* Category badge */}
                <span className="absolute top-3 right-3 bg-white/90 backdrop-blur px-2 py-1 rounded-md text-xs font-medium text-gray-700">
                  {template.category}
                </span>
                {/* Popular badge */}
                {template.popular && (
                  <span className="absolute top-3 left-3 bg-yellow-400 px-2 py-1 rounded-md text-xs font-medium text-yellow-900">
                    ⭐ Popular
                  </span>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">
                    {template.title}
                  </h3>
                  <button
                    className="text-gray-400 hover:text-yellow-500"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Star size={16} />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                  {template.description}
                </p>
                <button
                  className="w-full py-2 border border-teal-600 text-teal-600 rounded-lg text-sm font-medium hover:bg-teal-50 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTemplateSelect(template);
                  }}
                >
                  Use Template
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty state */}
        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <BookTemplate size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-2">No templates found</p>
            <p className="text-gray-400 text-sm">
              Try adjusting your search or category filter
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Database Screen
  const handleDeleteDatatable = async (id: string) => {
    try {
      await deleteDatatable(id);
      toast.success("Table deleted successfully");
      fetchDatatables();
    } catch (error) {
      console.error("Error deleting datatable:", error);
      toast.error("Failed to delete table");
    }
  };

  const handleCreateDatatable = async (data: {
    name: string;
    mongoCollectionName: string;
  }) => {
    await createDatatable(data);
    toast.success("Table created successfully");
    fetchDatatables();
  };

  // Filter datatables by search query
  const filteredDatatables = datatables.filter((dt) => {
    if (!datatableSearchQuery) return true;
    const query = datatableSearchQuery.toLowerCase();
    return (
      dt.name.toLowerCase().includes(query) ||
      dt.mongoCollectionName.toLowerCase().includes(query)
    );
  });

  const renderDatabase = () => {
    // If viewing a specific table's records
    if (selectedDatatable) {
      return (
        <div className="flex-1 overflow-auto">
          <div className="max-w-6xl mx-auto p-8">
            {/* Header with back button */}
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => setSelectedDatatable(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
              >
                <ChevronLeft size={20} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {selectedDatatable.name}
                </h1>
                <p className="text-gray-500 text-sm font-mono">
                  {selectedDatatable.mongoCollectionName}
                </p>
              </div>
              <div className="flex-1" />
              <button
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                onClick={() => {
                  toast.info("Add record feature coming soon");
                }}
              >
                <Plus size={18} />
                Add Record
              </button>
            </div>

            {/* Records Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {isLoadingRecords ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  <span className="ml-3 text-gray-500">Loading records...</span>
                </div>
              ) : datatableRecords.length === 0 ? (
                <div className="text-center py-12">
                  <Database size={48} className="mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-2">No records found</p>
                  <p className="text-gray-400 text-sm">
                    Add your first record to this table
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        {Object.keys(datatableRecords[0] || {}).map((key) => (
                          <th
                            key={key}
                            className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                          >
                            {key}
                          </th>
                        ))}
                        <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {datatableRecords.map((record, idx) => (
                        <tr
                          key={record._id || idx}
                          className="hover:bg-gray-50"
                        >
                          {Object.entries(record).map(([key, value]) => (
                            <td
                              key={key}
                              className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate"
                            >
                              {typeof value === "object"
                                ? JSON.stringify(value)
                                : String(value ?? "")}
                            </td>
                          ))}
                          <td className="px-6 py-4 text-right">
                            <button
                              className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-red-500"
                              onClick={() => {
                                if (record._id) {
                                  deleteRecord(
                                    selectedDatatable.mongoCollectionName,
                                    record._id,
                                  )
                                    .then(() => {
                                      toast.success("Record deleted");
                                      fetchDatatableRecords(
                                        selectedDatatable.mongoCollectionName,
                                      );
                                    })
                                    .catch(() =>
                                      toast.error("Failed to delete record"),
                                    );
                                }
                              }}
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination info */}
            {!isLoadingRecords && datatableRecords.length > 0 && (
              <div className="mt-4 text-sm text-gray-500">
                Showing {datatableRecords.length} of {totalRecords} records
              </div>
            )}
          </div>
        </div>
      );
    }

    // Main database list view
    return (
      <div className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Database</h1>
              <p className="text-gray-500 text-sm">
                Manage your data tables and collections
              </p>
            </div>
            <button
              onClick={() => setIsCreateDatatableModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus size={18} />
              New Table
            </button>
          </div>

          {/* Search and Refresh */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={datatableSearchQuery}
                onChange={(e) => setDatatableSearchQuery(e.target.value)}
                placeholder="Search tables..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <button
              onClick={() => fetchDatatables()}
              disabled={isLoadingDatatables}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw
                size={16}
                className={isLoadingDatatables ? "animate-spin" : ""}
              />
              Refresh
            </button>
          </div>

          {/* Table List */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {isLoadingDatatables ? (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Table Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Collection
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[1, 2, 3].map((i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-40"></div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="h-6 bg-gray-200 rounded w-16 ml-auto"></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : filteredDatatables.length === 0 ? (
              <div className="text-center py-12">
                <Database size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">No tables found</p>
                <button
                  onClick={() => setIsCreateDatatableModalOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <Plus size={16} />
                  Create your first table
                </button>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Table Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Collection
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredDatatables.map((dt) => (
                    <tr
                      key={dt.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedDatatable(dt)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <Database size={16} className="text-emerald-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {dt.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                        {dt.mongoCollectionName}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDatatable(dt.id);
                          }}
                          title="Delete table"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Total count */}
          {!isLoadingDatatables && filteredDatatables.length > 0 && (
            <div className="mt-4 text-sm text-gray-500">
              Showing {filteredDatatables.length} table
              {filteredDatatables.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Connectors Screen
  const renderConnectors = () => {
    // Filter templates based on type/category
    const filteredTemplates = designerTemplates.filter((t) => {
      // Search logic
      if (connectorSearchQuery) {
        const query = connectorSearchQuery.toLowerCase();
        const name = (t.name || "").toLowerCase();
        const type = (t.type || "").toLowerCase();
        const desc = (t.description || "").toLowerCase();
        const category = (t.category || "").toLowerCase();
        if (
          !name.includes(query) &&
          !type.includes(query) &&
          !desc.includes(query) &&
          !category.includes(query)
        ) {
          return false;
        }
      }

      if (connectorFilter === "All") return true;
      const type = (t.type || "").toLowerCase();
      const category = (t.category || "").toLowerCase();
      if (connectorFilter === "Manual") {
        return (
          type.includes("source") ||
          type.includes("trigger") ||
          type.includes("inbound") ||
          category.includes("inbound")
        );
      }
      if (connectorFilter === "Trigger") {
        return (
          type.includes("sink") ||
          type.includes("target") ||
          type.includes("action") ||
          type.includes("outbound") ||
          category.includes("outbound")
        );
      }
      return true;
    });

    return (
      <div className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Components</h1>
              <p className="text-gray-500 text-sm">
                Manage your integrations and connections
              </p>
            </div>
            <button
              onClick={() => setIsCreateConnectorModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus size={18} />
              Add Connector
            </button>
          </div>

          {/* Connector Library (Elements) */}
          <div className="flex items-center justify-between mb-4 gap-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Connector Library
            </h2>

            <div className="flex items-center gap-2">
              {/* Search Input */}
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  type="text"
                  placeholder="Search connectors..."
                  value={connectorSearchQuery}
                  onChange={(e) => setConnectorSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-64"
                />
              </div>

              {/* View Toggle */}
              <div className="flex bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setConnectorViewMode("grid")}
                  className={`p-1.5 rounded-md transition-shadow ${connectorViewMode === "grid" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"}`}
                  title="Grid View"
                >
                  <LayoutGrid size={16} />
                </button>
                <button
                  onClick={() => setConnectorViewMode("list")}
                  className={`p-1.5 rounded-md transition-shadow ${connectorViewMode === "list" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-900"}`}
                  title="List View"
                >
                  <List size={16} />
                </button>
              </div>

              {/* Filter Tabs */}
              <div className="flex bg-gray-100 p-1 rounded-lg">
                {(["All", "Manual", "Trigger"] as const).map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setConnectorFilter(filter)}
                    className={`px-3 py-1 text-sm font-medium rounded-md transition-shadow ${connectorFilter === filter
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-900"
                      } `}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loadingDesignerTemplates ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div>
              {filteredTemplates.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>No connectors found for {connectorFilter}</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {(() => {
                    const groupedTemplates = filteredTemplates.reduce(
                      (acc, template) => {
                        let category = template.category || "Uncategorized";
                        // Try to decode category if missing
                        try {
                          if (!template.category && template.jsonContent) {
                            const decoded = JSON.parse(
                              atob(template.jsonContent),
                            );
                            if (decoded.category) category = decoded.category;
                          }
                        } catch (e) { }

                        if (!acc[category]) acc[category] = [];
                        acc[category].push(template);
                        return acc;
                      },
                      {} as Record<string, typeof filteredTemplates>,
                    );

                    const sortedGroups = Object.entries(groupedTemplates).sort(
                      (a, b) => a[0].localeCompare(b[0]),
                    );
                    const pinnedTemplates = filteredTemplates.filter((t) =>
                      pinnedConnectorIds.includes(t.id),
                    );

                    return (
                      <>
                        {/* Pinned Connectors Section */}
                        {pinnedTemplates.length > 0 && (
                          <div className="mb-8 p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100">
                            <div className="flex items-center gap-2 mb-4 px-1">
                              <Pin
                                size={18}
                                className="text-indigo-600 fill-indigo-600"
                              />
                              <h3 className="text-lg font-bold text-indigo-900 uppercase tracking-wide">
                                Pinned
                              </h3>
                            </div>
                            <div
                              className={
                                connectorViewMode === "grid"
                                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                                  : "flex flex-col space-y-2"
                              }
                            >
                              {pinnedTemplates.map((template) => {
                                // Determine type for tag
                                const typeLower = (
                                  template.type || ""
                                ).toLowerCase();
                                const isInbound =
                                  typeLower.includes("source") ||
                                  typeLower.includes("trigger") ||
                                  typeLower.includes("inbound");
                                const isOutbound =
                                  typeLower.includes("sink") ||
                                  typeLower.includes("target") ||
                                  typeLower.includes("action") ||
                                  typeLower.includes("outbound");

                                // Extract icon/desc logic (duplicated for now to keep render local)
                                let iconUrl: string | undefined;
                                let description: string | undefined;
                                try {
                                  if (template.jsonContent) {
                                    const decoded = atob(template.jsonContent);
                                    const json = JSON.parse(decoded);
                                    if (json?.modelerTemplateIcon)
                                      iconUrl =
                                        typeof json.modelerTemplateIcon ===
                                          "string"
                                          ? json.modelerTemplateIcon
                                          : json.modelerTemplateIcon.value;
                                    if (json?.description)
                                      description = json.description;
                                  }
                                } catch (e) { }
                                const fallbackIcon =
                                  template.decodedContent?.icon;

                                if (connectorViewMode === "list") {
                                  return (
                                    <div
                                      key={template.id}
                                      className="group bg-white rounded-xl border-2 border-indigo-200 px-4 py-3 hover:border-indigo-400 hover:shadow-md transition-all duration-200 cursor-pointer flex items-center gap-4 relative"
                                      onClick={() =>
                                        handleConfigureConnector(template)
                                      }
                                    >
                                      <div
                                        className={`w-10 h-10 ${template.decodedContent?.color ? "" : "bg-gradient-to-br from-indigo-50 to-indigo-100"} ${template.decodedContent?.color || ""} rounded-lg flex items-center justify-center text-indigo-700 font-bold overflow-hidden shadow-sm ring-1 ring-black/5 flex-shrink-0`}
                                      >
                                        {iconUrl ? (
                                          <img
                                            src={iconUrl}
                                            alt={template.name}
                                            className="w-6 h-6 object-contain"
                                          />
                                        ) : fallbackIcon &&
                                          fallbackIcon.length === 1 ? (
                                          <span className="text-sm">
                                            {fallbackIcon}
                                          </span>
                                        ) : (
                                          <Shapes
                                            size={20}
                                            className="text-indigo-500"
                                          />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-0.5">
                                          <h3 className="text-sm font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
                                            {template.name}
                                          </h3>
                                          {isInbound && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-semibold uppercase border border-blue-100">
                                              Manual
                                            </span>
                                          )}
                                          {isOutbound && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 text-[10px] font-semibold uppercase border border-orange-100">
                                              Trigger
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-500 line-clamp-1">
                                          {template.description ||
                                            description ||
                                            "No description available"}
                                        </p>
                                      </div>
                                      <button
                                        onClick={(e) =>
                                          toggleConnectorPin(template.id, e)
                                        }
                                        className="text-indigo-500 hover:text-indigo-700 p-2 rounded-lg hover:bg-indigo-50 transition-colors"
                                        title="Unpin"
                                      >
                                        <Pin
                                          size={16}
                                          className="fill-current"
                                        />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleConfigureConnector(template);
                                        }}
                                        className="text-gray-400 hover:text-indigo-600 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                                      >
                                        <Pencil size={16} />
                                      </button>
                                    </div>
                                  );
                                }

                                return (
                                  <div
                                    key={template.id}
                                    className="group bg-white rounded-2xl border-2 border-indigo-200 p-4 hover:border-indigo-400 hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col h-full relative overflow-hidden"
                                  >
                                    <button
                                      onClick={(e) =>
                                        toggleConnectorPin(template.id, e)
                                      }
                                      className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/80 backdrop-blur shadow-sm text-indigo-500 hover:text-indigo-700 hover:bg-white transition-all transform hover:scale-110"
                                      title="Unpin"
                                    >
                                      <Pin size={14} className="fill-current" />
                                    </button>
                                    <div className="flex items-start justify-between mb-3">
                                      <div
                                        className={`w-12 h-12 ${template.decodedContent?.color ? "" : "bg-gradient-to-br from-indigo-50 to-indigo-100"} ${template.decodedContent?.color || ""} rounded-2xl flex items-center justify-center text-indigo-700 font-bold text-lg overflow-hidden shadow-sm ring-1 ring-black/5`}
                                      >
                                        {iconUrl ? (
                                          <img
                                            src={iconUrl}
                                            alt={template.name}
                                            className="w-8 h-8 object-contain"
                                          />
                                        ) : fallbackIcon &&
                                          fallbackIcon.length === 1 ? (
                                          <span>{fallbackIcon}</span>
                                        ) : (
                                          <Shapes
                                            size={26}
                                            className="text-indigo-500"
                                          />
                                        )}
                                      </div>
                                      <div className="flex gap-2">
                                        {isInbound && (
                                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-[10px] font-semibold tracking-wide uppercase border border-blue-100">
                                            Manual
                                          </span>
                                        )}
                                        {isOutbound && (
                                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-orange-50 text-orange-700 text-[10px] font-semibold tracking-wide uppercase border border-orange-100">
                                            Trigger
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="mb-3 flex-grow">
                                      <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-indigo-600 transition-colors">
                                        {template.name}
                                      </h3>
                                      <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                                        {template.description ||
                                          description ||
                                          "No description available"}
                                      </p>
                                    </div>
                                    <div className="flex items-center justify-between pt-3 border-t border-gray-50 mt-auto">
                                      <div className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                        <span className="text-xs font-medium text-gray-400 uppercase">
                                          {template.category || "Connector"}
                                        </span>
                                      </div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleConfigureConnector(template);
                                        }}
                                        className="opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1.5 rounded-lg shadow-sm flex items-center gap-1"
                                      >
                                        Configure <ArrowRight size={12} />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {sortedGroups.map(([category, templates]) => {
                          const unpinnedTemplates = templates.filter(
                            (t) => !pinnedConnectorIds.includes(t.id),
                          );
                          if (unpinnedTemplates.length === 0) return null;

                          return (
                            <div key={category}>
                              <h3 className="text-lg font-bold text-gray-800 mb-4 px-1 uppercase">
                                {category}
                              </h3>
                              <div
                                className={
                                  connectorViewMode === "grid"
                                    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                                    : "flex flex-col space-y-2"
                                }
                              >
                                {templates.map((template) => {
                                  // Determine type for tag
                                  const typeLower = (
                                    template.type || ""
                                  ).toLowerCase();
                                  const isInbound =
                                    typeLower.includes("source") ||
                                    typeLower.includes("trigger") ||
                                    typeLower.includes("inbound");
                                  const isOutbound =
                                    typeLower.includes("sink") ||
                                    typeLower.includes("target") ||
                                    typeLower.includes("action") ||
                                    typeLower.includes("outbound");

                                  // Extract icon and description from jsonContent (base64)
                                  let iconUrl: string | undefined;
                                  let description: string | undefined;
                                  try {
                                    if (template.jsonContent) {
                                      const decoded = atob(
                                        template.jsonContent,
                                      );
                                      const json = JSON.parse(decoded);
                                      // Check for either direct property or value property
                                      if (json?.modelerTemplateIcon) {
                                        iconUrl =
                                          typeof json.modelerTemplateIcon ===
                                            "string"
                                            ? json.modelerTemplateIcon
                                            : json.modelerTemplateIcon.value;
                                      }
                                      if (json?.description) {
                                        description = json.description;
                                      }
                                    }
                                  } catch (e) {
                                    // Ignore parsing errors
                                  }

                                  const fallbackIcon =
                                    template.decodedContent?.icon;

                                  if (connectorViewMode === "list") {
                                    return (
                                      <div
                                        key={template.id}
                                        className="group bg-white rounded-xl border border-gray-200 px-4 py-3 hover:border-indigo-300 hover:shadow-md transition-all duration-200 cursor-pointer flex items-center gap-4"
                                        onClick={() =>
                                          handleConfigureConnector(template)
                                        }
                                      >
                                        <div
                                          className={`w-10 h-10 ${template.decodedContent?.color ? "" : "bg-gradient-to-br from-gray-50 to-gray-100"} ${template.decodedContent?.color || ""} rounded-lg flex items-center justify-center text-gray-700 font-bold overflow-hidden shadow-sm ring-1 ring-black/5 flex-shrink-0`}
                                        >
                                          {iconUrl ? (
                                            <img
                                              src={iconUrl}
                                              alt={template.name}
                                              className="w-6 h-6 object-contain"
                                            />
                                          ) : fallbackIcon &&
                                            fallbackIcon.length === 1 ? (
                                            <span className="text-sm">
                                              {fallbackIcon}
                                            </span>
                                          ) : (
                                            <Shapes
                                              size={20}
                                              className="text-gray-500"
                                            />
                                          )}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-3 mb-0.5">
                                            <h3 className="text-sm font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
                                              {template.name}
                                            </h3>
                                            {isInbound && (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-semibold uppercase border border-blue-100">
                                                Manual
                                              </span>
                                            )}
                                            {isOutbound && (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 text-[10px] font-semibold uppercase border border-orange-100">
                                                Trigger
                                              </span>
                                            )}
                                          </div>
                                          <p className="text-xs text-gray-500 line-clamp-1">
                                            {template.description ||
                                              description ||
                                              "No description available"}
                                          </p>
                                        </div>

                                        <button
                                          onClick={(e) =>
                                            toggleConnectorPin(template.id, e)
                                          }
                                          className="text-gray-300 hover:text-indigo-500 p-2 rounded-lg hover:bg-gray-50 transition-colors opacity-0 group-hover:opacity-100"
                                          title="Pin"
                                        >
                                          <Pin size={16} />
                                        </button>

                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleConfigureConnector(template);
                                          }}
                                          className="text-gray-400 hover:text-indigo-600 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                          <Pencil size={16} />
                                        </button>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div
                                      key={template.id}
                                      className="group bg-white rounded-2xl border border-gray-200 p-4 hover:border-indigo-300 hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col h-full relative overflow-hidden"
                                    >
                                      <button
                                        onClick={(e) =>
                                          toggleConnectorPin(template.id, e)
                                        }
                                        className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/80 backdrop-blur shadow-sm text-gray-300 hover:text-indigo-500 hover:bg-white transition-all transform hover:scale-110 opacity-0 group-hover:opacity-100"
                                        title="Pin"
                                      >
                                        <Pin size={14} />
                                      </button>
                                      <div className="flex items-start justify-between mb-3">
                                        {/* Icon with Gradient Background */}
                                        <div
                                          className={`w-12 h-12 ${template.decodedContent?.color ? "" : "bg-gradient-to-br from-gray-50 to-gray-100"} ${template.decodedContent?.color || ""} rounded-2xl flex items-center justify-center text-gray-700 font-bold text-lg overflow-hidden shadow-sm ring-1 ring-black/5`}
                                        >
                                          {iconUrl ? (
                                            <img
                                              src={iconUrl}
                                              alt={template.name}
                                              className="w-8 h-8 object-contain"
                                            />
                                          ) : fallbackIcon &&
                                            fallbackIcon.length === 1 ? (
                                            <span>{fallbackIcon}</span>
                                          ) : (
                                            <Shapes
                                              size={26}
                                              className="text-gray-500"
                                            />
                                          )}
                                        </div>

                                        {/* Status Badges */}
                                        <div className="flex gap-2">
                                          {isInbound && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-[10px] font-semibold tracking-wide uppercase border border-blue-100">
                                              Manual
                                            </span>
                                          )}
                                          {isOutbound && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-orange-50 text-orange-700 text-[10px] font-semibold tracking-wide uppercase border border-orange-100">
                                              Trigger
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      <div className="mb-3 flex-grow">
                                        <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-indigo-600 transition-colors">
                                          {template.name}
                                        </h3>
                                        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                                          {/* Fallback description if not present */}
                                          {template.description ||
                                            description ||
                                            "No description available"}
                                        </p>
                                      </div>

                                      <div className="flex items-center justify-between pt-3 border-t border-gray-50 mt-auto">
                                        <div className="flex items-center gap-2">
                                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                          <span className="text-xs font-medium text-gray-400 uppercase">
                                            {template.category || "Connector"}
                                          </span>
                                        </div>

                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleConfigureConnector(template);
                                          }}
                                          className="opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1.5 rounded-lg shadow-sm flex items-center gap-1"
                                        >
                                          Configure <ArrowRight size={12} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Preferences Screen
  const renderPreferences = () => (
    <div className="flex-1 overflow-auto">
      <div className="max-w-3xl mx-auto p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Preferences</h1>
        <p className="text-gray-500 text-sm mb-8">
          Customize your Studio experience
        </p>

        {/* Editor Settings */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Shapes size={20} className="text-gray-500" />
            Editor Settings
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <div className="font-medium text-gray-900">
                  Auto-save workflows
                </div>
                <div className="text-sm text-gray-500">
                  Automatically save changes every 30 seconds
                </div>
              </div>
              <button className="relative w-12 h-6 bg-indigo-600 rounded-full transition-colors">
                <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow"></span>
              </button>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <div className="font-medium text-gray-900">Snap to grid</div>
                <div className="text-sm text-gray-500">
                  Align BPMN elements to grid when moving
                </div>
              </div>
              <button className="relative w-12 h-6 bg-indigo-600 rounded-full transition-colors">
                <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow"></span>
              </button>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <div className="font-medium text-gray-900">Show minimap</div>
                <div className="text-sm text-gray-500">
                  Display a minimap for navigating large diagrams
                </div>
              </div>
              <button className="relative w-12 h-6 bg-gray-200 rounded-full transition-colors">
                <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow"></span>
              </button>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="font-medium text-gray-900">
                  Keyboard shortcuts
                </div>
                <div className="text-sm text-gray-500">
                  Enable keyboard shortcuts for common actions
                </div>
              </div>
              <button className="relative w-12 h-6 bg-indigo-600 rounded-full transition-colors">
                <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow"></span>
              </button>
            </div>
          </div>
        </div>

        {/* Form Builder Settings */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText size={20} className="text-gray-500" />
            Form Builder
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <div className="font-medium text-gray-900">
                  Enable field validation
                </div>
                <div className="text-sm text-gray-500">
                  Add required validation by default on new fields
                </div>
              </div>
              <button className="relative w-12 h-6 bg-indigo-600 rounded-full transition-colors">
                <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow"></span>
              </button>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="font-medium text-gray-900">
                  Default form layout
                </div>
                <div className="text-sm text-gray-500">
                  Choose default layout for new forms
                </div>
              </div>
              <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option>Single Column</option>
                <option>Two Columns</option>
                <option>Wizard</option>
              </select>
            </div>
          </div>
        </div>

        {/* Connector Settings */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Plug size={20} className="text-gray-500" />
            Connector Defaults
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <div className="font-medium text-gray-900">
                  Connection timeout
                </div>
                <div className="text-sm text-gray-500">
                  Default timeout for connector requests
                </div>
              </div>
              <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option>30 seconds</option>
                <option>60 seconds</option>
                <option>120 seconds</option>
                <option>5 minutes</option>
              </select>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="font-medium text-gray-900">
                  Retry on failure
                </div>
                <div className="text-sm text-gray-500">
                  Automatically retry failed connector calls
                </div>
              </div>
              <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option>No retry</option>
                <option>1 retry</option>
                <option>3 retries</option>
                <option>5 retries</option>
              </select>
            </div>
          </div>
        </div>

        {/* Workflow Defaults */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Workflow size={20} className="text-gray-500" />
            Workflow Defaults
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <div className="font-medium text-gray-900">
                  Default trigger type
                </div>
                <div className="text-sm text-gray-500">
                  Set default trigger for new workflows
                </div>
              </div>
              <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option>Manual</option>
                <option>Timer</option>
                <option>Message</option>
                <option>Signal</option>
              </select>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <div className="font-medium text-gray-900">Version format</div>
                <div className="text-sm text-gray-500">
                  How versions are numbered for workflows
                </div>
              </div>
              <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option>Semantic (X.Y)</option>
                <option>Incremental (1, 2, 3...)</option>
                <option>Date-based</option>
              </select>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="font-medium text-gray-900">
                  Execution timeout
                </div>
                <div className="text-sm text-gray-500">
                  Maximum execution time for workflows
                </div>
              </div>
              <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option>5 minutes</option>
                <option>15 minutes</option>
                <option>30 minutes</option>
                <option>1 hour</option>
                <option>No limit</option>
              </select>
            </div>
          </div>
        </div>

        {/* Appearance */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Palette size={20} className="text-gray-500" />
            Appearance
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <div className="font-medium text-gray-900">Theme</div>
                <div className="text-sm text-gray-500">
                  Choose your preferred color theme
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  className="w-8 h-8 bg-white border-2 border-indigo-500 rounded-full flex items-center justify-center"
                  title="Light"
                >
                  <span className="sr-only">Light</span>
                </button>
                <button
                  className="w-8 h-8 bg-gray-900 border-2 border-transparent rounded-full hover:border-gray-400"
                  title="Dark"
                >
                  <span className="sr-only">Dark</span>
                </button>
                <button
                  className="w-8 h-8 bg-gradient-to-br from-gray-100 to-gray-400 border-2 border-transparent rounded-full hover:border-gray-400"
                  title="System"
                >
                  <span className="sr-only">System</span>
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <div className="font-medium text-gray-900">Compact view</div>
                <div className="text-sm text-gray-500">
                  Show more items in lists and tables
                </div>
              </div>
              <button className="relative w-12 h-6 bg-gray-200 rounded-full transition-colors">
                <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow"></span>
              </button>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="font-medium text-gray-900">
                  Sidebar position
                </div>
                <div className="text-sm text-gray-500">
                  Choose navigation sidebar placement
                </div>
              </div>
              <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option>Left</option>
                <option>Right</option>
              </select>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Bell size={20} className="text-gray-500" />
            Notifications
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <div className="font-medium text-gray-900">
                  Workflow failures
                </div>
                <div className="text-sm text-gray-500">
                  Get notified when a workflow execution fails
                </div>
              </div>
              <button className="relative w-12 h-6 bg-indigo-600 rounded-full transition-colors">
                <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow"></span>
              </button>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <div className="font-medium text-gray-900">
                  Deployment success
                </div>
                <div className="text-sm text-gray-500">
                  Get notified when workflows are deployed
                </div>
              </div>
              <button className="relative w-12 h-6 bg-indigo-600 rounded-full transition-colors">
                <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow"></span>
              </button>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <div className="font-medium text-gray-900">
                  Task assignments
                </div>
                <div className="text-sm text-gray-500">
                  Get notified when user tasks are assigned to you
                </div>
              </div>
              <button className="relative w-12 h-6 bg-indigo-600 rounded-full transition-colors">
                <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow"></span>
              </button>
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="font-medium text-gray-900">Weekly digest</div>
                <div className="text-sm text-gray-500">
                  Receive weekly summary of workflow activity
                </div>
              </div>
              <button className="relative w-12 h-6 bg-gray-200 rounded-full transition-colors">
                <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow"></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Help Guide Screen
  const renderHelpGuide = () => (
    <div className="flex-1 overflow-auto">
      <div className="max-w-4xl mx-auto p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Help Guide</h1>
        <p className="text-gray-500 text-sm mb-8">
          Learn how to get the most out of Studio
        </p>

        {/* Quick Start */}
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 mb-8 text-white">
          <h2 className="text-xl font-semibold mb-2">Getting Started</h2>
          <p className="text-indigo-100 mb-4">
            New to Studio? Watch our quick start guide to learn the basics.
          </p>
          <button className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-indigo-600 font-medium rounded-lg hover:bg-indigo-50 transition-colors">
            <Video size={18} />
            Watch Tutorial
          </button>
        </div>

        {/* Help Topics */}
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Help Topics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {[
            {
              icon: <BookOpen size={20} />,
              title: "Documentation",
              desc: "Explore detailed guides and API docs",
            },
            {
              icon: <Video size={20} />,
              title: "Video Tutorials",
              desc: "Learn through step-by-step videos",
            },
            {
              icon: <MessageCircle size={20} />,
              title: "Community Forum",
              desc: "Ask questions and share knowledge",
            },
            {
              icon: <Mail size={20} />,
              title: "Contact Support",
              desc: "Get help from our support team",
            },
          ].map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-shadow cursor-pointer group flex items-start gap-4"
            >
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 flex-shrink-0 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                {item.icon}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-indigo-600 transition-colors">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* FAQs */}
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Frequently Asked Questions
        </h2>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {[
            {
              q: "How do I create my first workflow?",
              a: 'Click "Start from scratch" on the Create page or choose a template to get started.',
            },
            {
              q: "Can I schedule workflows to run at specific times?",
              a: "Yes! Use the Timer trigger type to schedule workflows at specific intervals.",
            },
            {
              q: "How do I connect new apps and services?",
              a: 'Go to the Connectors page and click "Add Connector" to set up new integrations.',
            },
            {
              q: "What happens if a workflow fails?",
              a: "You'll receive a notification, and the workflow will be paused until you review the error.",
            },
          ].map((faq, index) => (
            <div
              key={index}
              className="p-5 border-b border-gray-100 last:border-0"
            >
              <button className="w-full flex items-center justify-between text-left">
                <span className="font-medium text-gray-900">{faq.q}</span>
                <ChevronRight size={16} className="text-gray-400" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Template Engine Screen
  const renderTemplateEngine = () => (
    <div className="flex-1 overflow-auto">
      <div className="max-w-4xl mx-auto p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Template Engine
        </h1>
        <p className="text-gray-500 text-sm">
          Manage and design document templates. (Coming soon)
        </p>
      </div>
    </div>
  );

  // Agentic AI Screen (Gen AI app list – or Medical Bot Studio when opened)
  const renderAgenticAi = () => {
    if (agenticAiSubView === "medical-bot") {
      return (
        <div className="flex-1 flex flex-col min-h-0">
          <MedicalBotStudio onBack={() => setAgenticAiSubView(null)} />
        </div>
      );
    }

    const filteredAgenticApps = agenticApps.filter((app) => {
      // Tab filter: All | Workflow | Agent
      if (agenticTabFilter !== "All") {
        const appType = (app.type || "workflow").toLowerCase();
        if (agenticTabFilter === "Workflow" && appType !== "workflow")
          return false;
        if (agenticTabFilter === "Agent" && appType !== "agent") return false;
      }
      // Created by me: show only custom apps
      if (agenticCreatedByMeOnly && app.isBuiltIn) return false;

      // Select Option: placeholder filter (currently only All / Built-in / Custom)
      if (agenticSelectOption === "Built-in" && !app.isBuiltIn) return false;
      if (agenticSelectOption === "Custom" && app.isBuiltIn) return false;

      // Search
      if (!agenticSearchQuery.trim()) return true;
      const q = agenticSearchQuery.toLowerCase();
      return (
        (app.title || "").toLowerCase().includes(q) ||
        (app.text || "").toLowerCase().includes(q) ||
        (app.type || "").toLowerCase().includes(q)
      );
    });

    return (
      <div className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto p-8">
          {/* Header – same pattern as Connectors */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Agentic AI</h1>
              <p className="text-gray-500 text-sm">
                Manage your Gen AI apps and workflows
              </p>
            </div>
            <button
              onClick={() => setIsCreateAppModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus size={18} />
              Create App
            </button>
          </div>

          {/* Filters (left) + Search+Tabs (right) */}
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
                <input
                  type="checkbox"
                  checked={agenticCreatedByMeOnly}
                  onChange={(e) => setAgenticCreatedByMeOnly(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                Created by me
              </label>

              <select
                value={agenticSelectOption}
                onChange={(e) => setAgenticSelectOption(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="All">Select Option</option>
                <option value="Built-in">Built-in</option>
                <option value="Custom">Custom</option>
              </select>
            </div>

            <div className="flex items-center justify-end gap-2">
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  type="text"
                  value={agenticSearchQuery}
                  onChange={(e) => setAgenticSearchQuery(e.target.value)}
                  placeholder="Search apps..."
                  className="pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-64"
                />
              </div>
              <div className="flex bg-gray-100 p-1 rounded-lg">
                {(["All", "Workflow", "Agent"] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setAgenticTabFilter(tab)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-shadow ${agenticTabFilter === tab
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-900"
                      }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* App list – table aligned with Forms/Workflows */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {filteredAgenticApps.length === 0 ? (
              <div className="text-center py-12">
                <Bot size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">No apps found</p>
                <button
                  onClick={() => setIsCreateAppModalOpen(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <Plus size={18} />
                  Create your first app
                </button>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAgenticApps.map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {app.title}
                          </div>
                          <div className="text-xs text-gray-500">
                            {app.text || "-"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 capitalize">
                          {app.type || "Workflow"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-indigo-500"
                            onClick={() => {
                              if (app.id === "medical-bot") {
                                setAgenticAiSubView("medical-bot");
                              } else {
                                toast.info("Studio view (coming soon)");
                              }
                            }}
                            title="Open app"
                          >
                            <Play size={16} />
                          </button>
                          {!app.isBuiltIn && (
                            <button
                              className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-red-500"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAgenticApp(app);
                              }}
                              title="Delete app"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeNavItem) {
      case "create":
        return renderOverview();
      case "workflows":
        return renderWorkflows();
      case "decisions":
        return <DecisionsPage />;
      case "rules":
        return <RulesPage />;
      case "forms":
        return renderForms();
      case "formBuilder":
        return renderFormBuilder();
      case "connectorBuilder":
        return (
          <ConnectorBuilderPage
            onBack={() => navigate("/studio/connectors")}
            connectorId={editingConnector?.id}
            initialData={editingConnector || undefined}
          />
        );
      case "editor":
        return renderEditor();
      case "templates":
        return renderTemplates();
      case "connectors":
        return renderConnectors();
      case "database":
        return renderDatabase();
      case "preferences":
        return renderPreferences();
      case "help":
        return renderHelpGuide();
      case "templateEngine":
        return renderTemplateEngine();
      case "agenticAi":
        return renderAgenticAi();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex">
      {/* Hide main Sidebar when in editor mode or form builder mode */}
      {activeNavItem !== "editor" &&
        activeNavItem !== "formBuilder" &&
        activeNavItem !== "connectorBuilder" && <Sidebar />}

      <div
        className={`flex-1 flex ${activeNavItem !== "editor" && activeNavItem !== "formBuilder" && activeNavItem !== "connectorBuilder" ? "ml-16" : ""}`}
      >
        {/* Studio Left Navigation - Hidden in editor/formBuilder mode */}
        {activeNavItem !== "editor" &&
          activeNavItem !== "formBuilder" &&
          activeNavItem !== "connectorBuilder" && (
            <div className="w-48 bg-white border-r border-gray-100 py-6 flex-shrink-0">
              <nav className="space-y-1 px-3">
                {studioNavItems.map((item) => {
                  const isActive =
                    item.key === activeNavItem ||
                    (item.key === "workflows" && activeNavItem === "editor");
                  return (
                    <button
                      key={item.key}
                      onClick={() => {
                        if (item.key === "create") {
                          navigate("/studio");
                        } else {
                          navigate(`/studio/${item.key}`);
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${isActive
                          ? "bg-indigo-50 text-indigo-700"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                        } `}
                    >
                      <span
                        className={
                          isActive ? "text-indigo-600" : "text-gray-400"
                        }
                      >
                        {item.icon}
                      </span>
                      {item.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          )}

        {/* Main Content with Transition */}
        <div className="flex-1 overflow-hidden flex flex-col bg-white">
          <div
            key={activeNavItem}
            className="flex-1 overflow-hidden flex flex-col animate-fadeSlideIn"
          >
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Modals */}
      <CreateWorkflowModal
        isOpen={isCreateWorkflowModalOpen}
        onClose={() => setIsCreateWorkflowModalOpen(false)}
        onContinue={handleCreateWorkflowContinue}
      />

      <CreateConnectorModal
        isOpen={isCreateConnectorModalOpen}
        onClose={() => setIsCreateConnectorModalOpen(false)}
        onCreate={handleCreateConnector}
      />

      {/* Create App modal (Agentic AI) – 3 options */}
      {isCreateAppModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsCreateAppModalOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Create App
              </h2>
              <button
                onClick={() => setIsCreateAppModalOpen(false)}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Choose how you want to create your Gen AI app.
            </p>
            <div className="space-y-2">
              <button
                onClick={() => {
                  setIsCreateAppModalOpen(false);
                  setIsCreateFromBlankModalOpen(true);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-indigo-200 text-left transition-colors"
              >
                <FileText size={20} className="text-indigo-600" />
                <div>
                  <div className="font-medium text-gray-900">
                    Create from Blank
                  </div>
                  <div className="text-xs text-gray-500">
                    Name, description, and workflow description
                  </div>
                </div>
              </button>
              <button
                onClick={() => {
                  setIsCreateAppModalOpen(false);
                  toast.info("Create from Template (coming soon)");
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-indigo-200 text-left transition-colors"
              >
                <BookTemplate size={20} className="text-indigo-600" />
                <div>
                  <div className="font-medium text-gray-900">
                    Create from Template
                  </div>
                  <div className="text-xs text-gray-500">
                    Start from a pre-built template
                  </div>
                </div>
              </button>
              <button
                onClick={() => {
                  setIsCreateAppModalOpen(false);
                  toast.info("Import DSL file (coming soon)");
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-indigo-200 text-left transition-colors"
              >
                <Upload size={20} className="text-indigo-600" />
                <div>
                  <div className="font-medium text-gray-900">
                    Import DSL file
                  </div>
                  <div className="text-xs text-gray-500">
                    Upload an existing DSL definition
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create from Blank – Name, Description, Workflow Description */}
      {isCreateFromBlankModalOpen && (
        <div className="fixed inset-0 z-[51] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setIsCreateFromBlankModalOpen(false);
              setNewAppName("");
              setNewAppDesc("");
              setNewAppWorkflowDesc("");
            }}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Create New App
              </h2>
              <button
                onClick={() => {
                  setIsCreateFromBlankModalOpen(false);
                  setNewAppName("");
                  setNewAppDesc("");
                  setNewAppWorkflowDesc("");
                }}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={newAppName}
                  onChange={(e) => setNewAppName(e.target.value)}
                  placeholder="e.g. Support Bot"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newAppDesc}
                  onChange={(e) => setNewAppDesc(e.target.value)}
                  placeholder="Brief description of the app"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Workflow Description
                </label>
                <textarea
                  value={newAppWorkflowDesc}
                  onChange={(e) => setNewAppWorkflowDesc(e.target.value)}
                  placeholder="e.g. medical, support, loan (optional – used to set workflow type)"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => {
                  setIsCreateFromBlankModalOpen(false);
                  setNewAppName("");
                  setNewAppDesc("");
                  setNewAppWorkflowDesc("");
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFromBlankSubmit}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      <CreateFormModal
        isOpen={isCreateFormModalOpen}
        onClose={() => setIsCreateFormModalOpen(false)}
        onFormCreated={(id: string, slug: string) => {
          setEditingFormId(id);
          setActiveNavItem("formBuilder");
          navigate(`/studio/forms/${slug}`);
        }}
      />

      <CreateDatatableModal
        isOpen={isCreateDatatableModalOpen}
        onClose={() => setIsCreateDatatableModalOpen(false)}
        onCreate={handleCreateDatatable}
      />
      {/* Template Name Modal */}
      {isTemplateModalOpen && selectedTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setIsTemplateModalOpen(false);
              setSelectedTemplate(null);
              setTemplateWorkflowName("");
            }}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            {/* Header with gradient */}
            <div
              className={`h-24 bg-gradient-to-br ${selectedTemplate.gradient} rounded-t-2xl flex items-center justify-center`}
            >
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {selectedTemplate.icons[0].letter ||
                    selectedTemplate.icons[0].icon}
                </div>
                <ArrowRight size={16} className="text-white/60" />
                <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {selectedTemplate.icons[1].letter ||
                    selectedTemplate.icons[1].icon}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-1">
                {selectedTemplate.title}
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                {selectedTemplate.description}
              </p>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workflow Name
                </label>
                <input
                  type="text"
                  value={templateWorkflowName}
                  onChange={(e) => setTemplateWorkflowName(e.target.value)}
                  placeholder="Enter workflow name..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setIsTemplateModalOpen(false);
                    setSelectedTemplate(null);
                    setTemplateWorkflowName("");
                  }}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFromTemplate}
                  disabled={
                    !templateWorkflowName.trim() || isCreatingFromTemplate
                  }
                  className="flex-1 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg text-sm font-medium hover:from-teal-600 hover:to-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingFromTemplate ? "Creating..." : "Create Workflow"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="text-red-600" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Delete Workflow?
                  </h3>
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete{" "}
                    <span className="font-semibold text-gray-900">
                      "{workflowToDelete?.name}"
                    </span>
                    ? This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setWorkflowToDelete(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (workflowToDelete) {
                      handleDeleteWorkflow(workflowToDelete.id);
                      setIsDeleteModalOpen(false);
                      setWorkflowToDelete(null);
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm"
                >
                  Delete Workflow
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deploy Modal */}
      <DeployModal
        isOpen={isDeployModalOpen}
        onClose={() => setIsDeployModalOpen(false)}
        workflow={selectedWorkflow}
        bpmnXml={deployBpmnXml}
        onDeploySuccess={handleDeploySuccess}
      />
      {/* Delete Form Modal */}
      <DeleteFormModal
        isOpen={!!formToDelete}
        form={formToDelete}
        isDeleting={isDeletingForm}
        onClose={() => setFormToDelete(null)}
        onConfirm={handleConfirmDeleteForm}
      />

      {/* AI Assistant - Portal render so it never mutates the BPMN canvas DOM tree */}
      {isAiDrawerOpen &&
        createPortal(
          <AiChatSidebar
            isOpen={isAiDrawerVisible}
            onClose={handleCloseAiDrawer}
            bpmnXml={currentBpmnXml}
            formJson={currentFormJson}
            onBpmnModified={async (xml) => {
              if (editorRef.current && editorRef.current.importXml) {
                // Enrich AI-generated service tasks with template metadata
                const enrichedXml = await enrichServiceTasksWithTemplates(xml);
                editorRef.current.importXml(enrichedXml);
                setCurrentBpmnXml(enrichedXml);
              }
            }}
            onFormModified={(json) => {
              setCurrentFormJson(json);
            }}
          />,
          document.body,
        )}

      {/* Start Process Modal */}
      <StartProcessDialog
        isOpen={isStartProcessOpen}
        onClose={() => setIsStartProcessOpen(false)}
        definitionId={startProcessDefId}
        name={startProcessName}
        version={startProcessVersion}
        onStarted={(id) => {
          // Provide a link to view instance when started seamlessly
          toast.success(
            () => (
              <div className="flex flex-col gap-1">
                <div>Process instance started:</div>
                <button
                  onClick={() => navigate(`/processes/instance/${id}`)}
                  className="text-indigo-600 font-medium text-left hover:underline"
                >
                  View {id}
                </button>
              </div>
            ),
            { duration: 5000 }
          );
        }}
      />
    </div>
  );
};

export default StudioPage;
